export function toTwo(n: number): string {
  return n.toString().padStart(2, '0');
}

export function addMinutesToTime(hhmm: string, minutesToAdd: number): string {
  const [hh, mm] = hhmm.split(':').map(Number);
  let total = hh * 60 + mm + minutesToAdd;
  if (total < 0) total = 0;
  const nh = Math.floor(total / 60) % 24;
  const nm = total % 60;
  return `${toTwo(nh)}:${toTwo(nm)}`;
}

export function timeToMinutes(hhmm: string): number {
  const [hh, mm] = hhmm.split(':').map(Number);
  return hh * 60 + mm;
}

export function getCurrentWeek(): { start: Date; end: Date } {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  const day = d.getDay();
  const diffToMonday = (day + 6) % 7;
  const start = new Date(d);
  start.setDate(d.getDate() - diffToMonday);
  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  return { start, end };
}

export function mapBackendDayToWeekday(backendDay: number): number {
  if (Number.isFinite(backendDay)) {
    const d = Math.trunc(backendDay);
    if (d >= 1 && d <= 7) return (d + 6) % 7;
    if (d >= 0 && d <= 6) return d;
  }
  return 0;
}

export function getSpanishWeekdayShort(date: Date, edit?: boolean): string {
  const idx = date.getDay();
  const names = !edit ? ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'] : ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
  return names[idx];
}

export function isoToLocalParts(iso: string): { date: string; time: string } {
  const parsed = new Date(iso);
  // A missing/unparseable `iso` must not surface as "NaN-NaN-NaN" in the UI —
  // fall back to "now" instead.
  const d = Number.isNaN(parsed.getTime()) ? new Date() : parsed;
  const yyyy = d.getFullYear();
  const mm = toTwo(d.getMonth() + 1);
  const dd = toTwo(d.getDate());
  const hh = toTwo(d.getHours());
  const mi = toTwo(d.getMinutes());
  return { date: `${yyyy}-${mm}-${dd}`, time: `${hh}:${mi}` };
}

// Formatea una fecha a dd/mm/yyyy. Acepta 'yyyy-mm-dd', Date u otros strings parseables
export function formatDdMmYyyy(input: string | Date): string {
  try {
    if (typeof input === 'string') {
      const m = input.match(/^(\d{4})-(\d{2})-(\d{2})$/);
      if (m) return `${m[3]}/${m[2]}/${m[1]}`;
      const d = new Date(input);
      if (!isNaN(d.getTime())) {
        const dd = toTwo(d.getDate());
        const mm = toTwo(d.getMonth() + 1);
        const yyyy = d.getFullYear();
        return `${dd}/${mm}/${yyyy}`;
      }
      return input;
    } else {
      const d = input;
      const dd = toTwo(d.getDate());
      const mm = toTwo(d.getMonth() + 1);
      const yyyy = d.getFullYear();
      return `${dd}/${mm}/${yyyy}`;
    }
  } catch {
    return String(input);
  }
}

/**
 * Formatea una fecha al mismo formato que usa la grilla del calendario:
 * "Vie 27 mar" — weekday abreviado + día + mes abreviado en español.
 * Acepta strings 'yyyy-mm-dd'.
 */
export function formatCalendarDay(dateStr: string): string {
  const [y, m, d] = dateStr.split('-').map(Number);
  const date = new Date(y, (m || 1) - 1, d || 1);
  return `${getSpanishWeekdayShort(date)} ${date.getDate()} ${date.toLocaleDateString('es-ES', { month: 'short' })}`;
}

/**
 * Formatea una fecha como "Viernes 27/03/2026" (nombre completo del día + DD/MM/YYYY).
 * Para encabezados con espacio suficiente.
 * Acepta strings 'yyyy-mm-dd' o ISO con hora.
 */
export function formatDayDdMmYyyy(dateStr?: string | null): string {
  if (!dateStr) return '—';
  const WEEKDAYS = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
  const m = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) {
    const date = new Date(parseInt(m[1]), parseInt(m[2]) - 1, parseInt(m[3]));
    return `${WEEKDAYS[date.getDay()]} ${m[3]}/${m[2]}/${m[1]}`;
  }
  const d = new Date(dateStr);
  if (!isNaN(d.getTime())) {
    const dd = toTwo(d.getDate());
    const mm = toTwo(d.getMonth() + 1);
    return `${WEEKDAYS[d.getDay()]} ${dd}/${mm}/${d.getFullYear()}`;
  }
  return '—';
}

export interface AvailabilityDay {
  date: string;
  schedules: { startTime: string; endTime: string }[];
}

export function generateAvailability(
  startOfWeek: Date,
  _endOfWeek: Date,
  _template: unknown[],
  _sessions: unknown[]
): AvailabilityDay[] {
  const days: AvailabilityDay[] = [];
  const dayCount = 7;
  for (let i = 0; i < dayCount; i++) {
    const date = new Date(startOfWeek);
    date.setDate(startOfWeek.getDate() + i);
    const yyyy = date.getFullYear();
    const mm = toTwo(date.getMonth() + 1);
    const dd = toTwo(date.getDate());
    const dateStr = `${yyyy}-${mm}-${dd}`;
    days.push({ date: dateStr, schedules: [] });
  }
  return days;
}


